import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'

import { useResizeEffect } from '@hooks/useResizeEffect'

export interface HorizontalScroll<T = HTMLElement> {
  scrollContainerRef: MutableRefObject<T | null>
  scrollContainerInnerRef: MutableRefObject<T | null>
  hasScrolledToEnd: boolean
  hasScrolledToStart: boolean
  isScrollable: boolean
}

interface HorizontalScrollParams {
  shouldRestorePosition?: boolean
  dependencies?: any[]
}

const getScrolledFromStart = (el: HTMLElement) => {
  const { scrollLeft, clientWidth, scrollWidth } = el
  const maxScrollLeft = Math.max(0, scrollWidth - clientWidth)
  const scrolledAmount = Math.min(Math.abs(scrollLeft), maxScrollLeft)

  return { scrolledAmount, maxScrollLeft }
}

export const useHorizontalScroll = <T extends HTMLElement>(
  params: HorizontalScrollParams = {}
): HorizontalScroll<T> => {
  const { shouldRestorePosition = false, dependencies = [] } = params

  const scrollContainerRef = useRef<T | null>(null)
  const scrollContainerInnerRef = useRef<T | null>(null)

  const prevScrollWidthRef = useRef(0)
  const prevScrollLeftRef = useRef(0)
  const lastScrollableWidthRef = useRef(0)

  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false)
  const [hasScrolledToStart, setHasScrolledToStart] = useState(true)
  const [isScrollable, setIsScrollable] = useState(false)
  const [lastManualScroll, setLastManualScroll] = useState(0)
  const [currentScrollLeft, setCurrentScrollLeft] = useState(0)

  const isRestoringRef = useRef(false)

  const restoreRaf1Ref = useRef<number | null>(null)
  const restoreRaf2Ref = useRef<number | null>(null)

  const checkIfScrolledToAnyEnd = useCallback(() => {
    const el = scrollContainerRef.current
    if (!el) return

    const { scrolledAmount, maxScrollLeft } = getScrolledFromStart(el)

    setHasScrolledToEnd(Math.abs(scrolledAmount - maxScrollLeft) <= 1)
    setHasScrolledToStart(scrolledAmount <= 1)
  }, [])

  const updateScrollableState = useCallback(() => {
    const el = scrollContainerRef.current
    if (!el) return

    const containerWidth = el.clientWidth
    const innerWidth = el.scrollWidth
    setIsScrollable(innerWidth > containerWidth)

    checkIfScrolledToAnyEnd()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIfScrolledToAnyEnd, ...dependencies])

  const applyRestore = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return false

    const newScrollWidth = container.scrollWidth
    const maxScrollLeft = Math.max(0, newScrollWidth - container.clientWidth)

    if (prevScrollWidthRef.current === 0) {
      prevScrollWidthRef.current = newScrollWidth
      if (maxScrollLeft > 0) lastScrollableWidthRef.current = newScrollWidth
      return false
    }

    if (maxScrollLeft === 0) {
      prevScrollWidthRef.current = newScrollWidth
      return false
    }

    const target = Math.min(prevScrollLeftRef.current, maxScrollLeft)

    isRestoringRef.current = true
    try {
      container.scrollTo?.({ left: target, behavior: 'smooth' })
    } catch {
      container.scrollLeft = target
    } finally {
      const SMOOTH_WINDOW_MS = 300
      window.setTimeout(() => {
        isRestoringRef.current = false
      }, SMOOTH_WINDOW_MS)
    }

    prevScrollWidthRef.current = newScrollWidth
    lastScrollableWidthRef.current = newScrollWidth
    return true
  }, [])

  const restoreScrollPosition = useCallback(() => {
    let applied = applyRestore()

    restoreRaf1Ref.current = requestAnimationFrame(() => {
      applied = applyRestore() || applied

      restoreRaf2Ref.current = requestAnimationFrame(() => {
        applyRestore()
      })
    })
  }, [applyRestore])

  const handleChange = useCallback(
    (shouldRestore?: boolean) => {
      if (shouldRestore) {
        restoreScrollPosition()
      }
      checkIfScrolledToAnyEnd()
      updateScrollableState()
    },
    [checkIfScrolledToAnyEnd, updateScrollableState, restoreScrollPosition]
  )

  useResizeEffect(handleChange)

  useEffect(() => {
    if (lastManualScroll > 50 && currentScrollLeft === 0 && isScrollable) {
      const el = scrollContainerRef.current
      if (el) el.scrollLeft = lastManualScroll
      setLastManualScroll(0)
    }
  }, [lastManualScroll, currentScrollLeft, isScrollable])

  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return

    const handleScroll = () => {
      checkIfScrolledToAnyEnd()

      const container = scrollContainerRef.current
      const left = container?.scrollLeft ?? 0
      const scrollWidth = container?.scrollWidth ?? 0
      const clientWidth = container?.clientWidth ?? 0
      const hasOverflow = scrollWidth > clientWidth

      setCurrentScrollLeft(left)

      if (!isRestoringRef.current) {
        if (left > 0) setLastManualScroll(left)
        if (hasOverflow) prevScrollLeftRef.current = left
      }
    }

    el.addEventListener('scroll', handleScroll)
    return () => {
      el.removeEventListener('scroll', handleScroll)
    }
  }, [checkIfScrolledToAnyEnd])

  useEffect(() => {
    if (!shouldRestorePosition) return
    restoreScrollPosition()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, shouldRestorePosition])

  useEffect(() => {
    return () => {
      if (restoreRaf1Ref.current != null) cancelAnimationFrame(restoreRaf1Ref.current)
      if (restoreRaf2Ref.current != null) cancelAnimationFrame(restoreRaf2Ref.current)
    }
  }, [])

  useEffect(() => {
    const observer = new ResizeObserver(() => handleChange(shouldRestorePosition))
    const container = scrollContainerRef.current
    const inner = scrollContainerInnerRef.current

    if (inner) observer.observe(inner)
    if (container) observer.observe(container)

    handleChange()

    return () => observer.disconnect()
  }, [handleChange, shouldRestorePosition])

  return {
    scrollContainerRef,
    scrollContainerInnerRef,
    hasScrolledToEnd,
    hasScrolledToStart,
    isScrollable,
  }
}
